import { ChartData, ChartOptions } from "chart.js";

// config
import Config from "./Config";

// schema
import { IFactorComparisonPoint } from "../../schema";
// custom chart tooltip rendering function
import createExternalTooltip from "./createExternalTooltip";

export interface UseScatterChartProps {
	/** Label for the X Axis */
	xAxisLabel: string;
	/** Label for the Y Axis */
	yAxisLabel: string;
	/** Data to plot on the chart */
	data: IFactorComparisonPoint[];
	/** Function to return a color based on the value of the point */
	getPointColor?: (point: IFactorComparisonPoint) => string;
}

/**
 * A hook to return the data and options for a scatter chart
 * that uses the faxtors comparison api
 * also offers several methods to help with chart rendering
 */
const useScatterChart = (props: UseScatterChartProps) => {
	const { xAxisLabel, yAxisLabel, data, getPointColor = Config.getPointColor } = props;

	// default options for the chart
	const options: ChartOptions<"scatter"> = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			tooltip: {
				enabled: false,
				external: (context) => createExternalTooltip(context, { xAxisLabel, yAxisLabel }, getPointColor),
			},
			legend: {
				display: false,
			},
		},
		clip: false, // keeps points from being cut off at chart edges
		scales: {
			y: {
				title: {
					display: true,
					text: yAxisLabel,
				},
				...Config.DEFAULT_AXIS_OPTIONS,
			},
			x: {
				title: {
					display: true,
					text: xAxisLabel,
				},
				...Config.DEFAULT_AXIS_OPTIONS,
			},
		},
	};

	// helper function to return a new options object with the scales modified
	// used to scale the x axis to -100 - 100 for eNPS
	const getScaledOptions = (scaleX: any, scaleY: any) => {
		return {
			...options,
			scales: {
				...options.scales,
				x: {
					...options.scales?.x,
					...scaleX,
				},
				y: {
					...options.scales?.y,
					...scaleY,
				},
			},
		} as ChartOptions<"scatter">;
	};

	// helper function to return the data in the format that the chart expects
	const prepareDataForChart = (): ChartData<"scatter", IFactorComparisonPoint[]> => {
		return {
			datasets: [
				{
					...Config.DATASET_OPTIONS,
					pointBackgroundColor: (context) => {
						const { dataIndex } = context;
						return getPointColor(data[dataIndex]);
					},
					data,
				},
			],
		};
	};

	return { options, prepareDataForChart, getScaledOptions };
};

export default useScatterChart;
